import React, { useEffect, useMemo, useState } from 'react';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import LicenceStore from 'state/LicenceStore';
import { Loader } from 'components/atoms/Loader';
import { useLang } from 'models/langContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EDictionaryTypes } from 'models/dictionary';
import { inNextTick } from 'utils/stateUtils';
import { useVaryLicence } from './hooks';
import { useLegalEntities } from '../hooks';
import CaseAddForm, { EAddCaseType } from 'pages/CaseManagementPage/CasesPage/CaseAddForm';
import { isPathEqualToRouteName } from 'utils/route';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';

const LegalEntityLicenceVaryPage = () => {
  const { t } = useTranslations();
  const { pathname, state: routerState }: any = useLocation();
  const [selectedPrice, setSelectedPriceInternal] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [viewFormVary, setViewFormVary] = useState(false);
  const [showPricesTable, setShowPricesTable] = useState(false);
  const [isFirearmDataLoading, setIsFirearmDataLoading] = useState(true);
  const [hasLoadedWeapons, setHasLoadedWeapons] = useState(false);
  const [shouldPreserveWeapons, setShouldPreserveWeapons] = useState(false);
  const [hasHydratedWeapons, setHasHydratedWeapons] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);
  const [type, setType] = useState<LETypes | null>(null);
  const [typeLink, setTypeLink] = useState<ELegalEntityTypes | null>(null);

  const setSelectedPrice = (value: string) => {
    setSelectedPriceInternal(value);
  };
  const navigate = useNavigate();
  const query = useQuery();
  const {
    id = '',
    licenceId = '',
    caseId = '',
  } = useParams<{
    id: string;
    licenceId: string;
    caseId: string;
  }>();
  const expand: string | null = query.get('expand');
  const { getProperties } = useGlobalProperty();
  const { selectedLanguage, translations } = useLang();
  const { loadDictionaries } = useVaryLicence();

  const {
    getLicenceApplicationProccessData,
    licencePrices,
    getLicenceData,
    licenceDetails,
    varyLicence,
  } = useVaryLicence();

  const {
    getLicenceAttachmentsData,
    licenceAttachments,
    getLicenceWeaponData,
    licenceWeapon,
  } = useLegalEntities();

  useEffect(() => {
    if (licenceDetails && licenceDetails.legal?.type) {
      setType(licenceDetails.legal?.type);
      switch (licenceDetails.legal?.type) {
        case LETypes.individual:
          setTypeLink(ELegalEntityTypes.individual);
          break;
        case LETypes.organization:
          setTypeLink(ELegalEntityTypes.organisation);
          break;
        default:
          break;
      }
    }
  }, [licenceDetails]);

  const isFromLE = isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityLicenceVary);
  const isFromLEList = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityLicenceVaryFromLE,
  );
  const isFromLicence = isPathEqualToRouteName(pathname, ERouteLinks.DetailLicenceVary);
  const isFromLECase = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityCaseLicenceVaryPage,
  );
  const isFromCase = isPathEqualToRouteName(pathname, ERouteLinks.CaseVaryLicencePage);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.applicationProcessTypes]);
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      if (licenceId) {
        setIsLoading(true);
        setIsFirearmDataLoading(true);
        setHasLoadedWeapons(false);
        setHasHydratedWeapons(false);
        await Promise.all([
          getLicenceData(licenceId),
          getLicenceAttachmentsData(licenceId),
          getLicenceWeaponData(licenceId),
        ]);
        setHasLoadedWeapons(true);

        const storeSelectedPrice = LicenceStore.getSelectedPrice();
        const currentLicenceId = LicenceStore.getValue('licenceId');
        const hasStoreData = storeSelectedPrice && currentLicenceId === licenceId;
        const isReturningFromWeaponLinking = routerState?.saveToStory === true;

        if (hasStoreData && isReturningFromWeaponLinking) {
          setSelectedPrice(storeSelectedPrice);
          setViewFormVary(true);
          setIsLoading(false);
          setShouldPreserveWeapons(true);
        } else {
          LicenceStore.clearStore();
          setViewFormVary(false);
          setSelectedPrice('');
          setShouldPreserveWeapons(false);
        }
      }
    })();
  }, [licenceId]);

  useEffect(() => {
    if (!viewFormVary || !licenceId) {
      return;
    }

    if (!hasLoadedWeapons) {
      return;
    }

    if (shouldPreserveWeapons) {
      return;
    }

    if (hasHydratedWeapons) {
      return;
    }

    inNextTick(() => {
      const firearmLinkToBeRemovedIds =
        LicenceStore.getFormFirearmLinkToBeRemovedIds(licenceId);
      const sourceWeapons = Array.isArray(licenceWeapon) ? licenceWeapon : [];
      const filteredWeapons = firearmLinkToBeRemovedIds.length
        ? sourceWeapons.filter((item) => !firearmLinkToBeRemovedIds.includes(item.uniqueId))
        : sourceWeapons;

      LicenceStore.setFormFirearmCurrentLink(filteredWeapons, licenceId);
      setHasHydratedWeapons(true);
    });
  }, [
    licenceWeapon,
    viewFormVary,
    licenceId,
    hasLoadedWeapons,
    shouldPreserveWeapons,
    hasHydratedWeapons,
  ]);

  useEffect(() => {
    if (licenceAttachments?.content.length) {
      const attachmentsToDeleteIds =
        LicenceStore.getFormLicenceAttachmentsToDeleteIds(licenceId);
      if (attachmentsToDeleteIds.length) {
        const attachmentWithoutDelete = licenceAttachments?.content.filter(
          (item) => !attachmentsToDeleteIds.includes(item.uniqueId),
        );
        LicenceStore.setFormLicenceAttachmentsData(attachmentWithoutDelete);
      } else {
        LicenceStore.setFormLicenceAttachmentsData(licenceAttachments?.content);
      }
    }
  }, [licenceAttachments]);

  useEffect(() => {
    if (translations) {
      if (isFromLE || isFromLEList || isFromLECase) {
        const breadcrumbsBasic = getBreadcrumbsBasic(
          true,
          typeLink,
          t,
          false,
          licenceDetails?.legal?.uniqueId ?? undefined,
          isFromLE || isFromLEList ? 'licences' : undefined,
        );

        setBreadcrumbs([
          ...breadcrumbsBasic,
          ...(isFromLECase
            ? [
                {
                  label: t(Elabels.caseDetailsHeader),
                  route: `${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                    ':id',
                    id as string,
                  ).replace(':caseId', caseId)}?type=${typeLink}&expand=case`,
                },
                {
                  label: t('legalEntityDetails.view.licence.label'),
                  route: `${ERouteLinks.LegalEntityCaseDetailLicencePage.replace(
                    ':id',
                    id as string,
                  )
                    .replace(':caseId', caseId)
                    .replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`,
                },
              ]
            : isFromLEList
              ? []
              : [
                  {
                    label: t('legalEntityDetails.view.licence.label'),
                    route: `${ERouteLinks.LegalEntityViewLicence.replace(
                      ':id',
                      id as string,
                    ).replace(':licenceId', licenceId)}?type=${typeLink}`,
                  },
                ]),
        ]);
      } else if (isFromLicence) {
        setBreadcrumbs([
          { label: t('module.licences.name'), route: ERouteLinks.LicencesPage },
          {
            label: t('legalEntityDetails.view.licence.label'),
            route: `${ERouteLinks.DetailLicence.replace(':licenceId', licenceId)}?type=${typeLink}`,
          },
        ]);
      } else if (isFromCase) {
        const breadcrumbsBasic = getBreadcrumbsBasic(false, null, t);
        setBreadcrumbs([
          ...breadcrumbsBasic,
          {
            label: t(Elabels.caseDetailsHeader),
            route: `${ERouteLinks.CasesDetailsPage.replace(':id', id as string).replace(
              ':licenceId',
              licenceId,
            )}?type=${typeLink}&expand=${expand}`,
          },
          {
            label: t('legalEntityDetails.view.licence.label'),
            route: `${ERouteLinks.CaseDetailLicencePage.replace(':id', id as string).replace(
              ':licenceId',
              licenceId,
            )}?type=${typeLink}&expand=${expand}`,
          },
        ]);
      }
    }
  }, [isFromLE, isFromLEList, expand, id, licenceId, typeLink, translations, licenceDetails]);

  useEffect(() => {
    (async function init() {
      if (licenceDetails) {
        setIsLoading(true);
        setIsLoadingPrices(true);
        await getLicenceApplicationProccessData(licenceDetails.weaponLicenceTemplateUniqueId);
        // Don't set isLoading to false here - let the auto-selection logic handle it
        setIsLoadingPrices(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenceDetails?.weaponLicenceTemplateUniqueId]);

  const backLink = () => {
    LicenceStore.clearStore();
    let backUrl = '';
    if (isFromLE || isFromLEList || isFromLECase) {
      if (isFromLECase) {
        backUrl = `${ERouteLinks.LegalEntityCaseDetailLicencePage.replace(':id', id as string)
          .replace(':caseId', caseId)
          .replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`;
      } else if (isFromLEList) {
        backUrl = `${ERouteLinks.ViewLegalEntity.replace(
          ':id',
          id as string,
        )}?type=${typeLink}&expand=licences`;
      } else {
        backUrl = `${ERouteLinks.LegalEntityViewLicence.replace(':id', id as string).replace(
          ':licenceId',
          licenceId,
        )}?type=${typeLink}&expand=${expand}`;
      }
    } else if (isFromLicence) {
      backUrl = `${ERouteLinks.DetailLicence.replace(':licenceId', licenceId)}?type=${typeLink}`;
    } else if (isFromCase) {
      backUrl = `${ERouteLinks.CaseDetailLicencePage.replace(
        ':caseId',
        caseId as string,
      ).replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`;
    }
    navigate(backUrl);
  };

  useEffect(() => {
    if (!licencePrices) return;

    if (licencePrices.length === 1 && !viewFormVary && selectedPrice === '') {
      setSelectedPrice(licencePrices[0].uniqueId);
      LicenceStore.setSelectedPrice(licencePrices[0].uniqueId);
      LicenceStore.setValue('licenceId', licenceId);
      setViewFormVary(true);
      setShowPricesTable(false);
    } else if (licencePrices.length > 1 && !viewFormVary) {
      if (!showPricesTable) {
        setShowPricesTable(true);
      }
    } else if (licencePrices.length === 0 && !viewFormVary) {
      setShowPricesTable(true);
    }
    if (isLoading) {
      setIsLoading(false);
    }
  }, [licencePrices, viewFormVary, selectedPrice, licenceId]);

  const submitForm = async (data: any) => {
    if (licenceDetails) {
      const result = await varyLicence(data, licenceDetails.uniqueId);
      if (result) {
        navigate(
          isFromLE
            ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':id', id as string).replace(':caseId', result as string)}?type=${typeLink}`
            : ERouteLinks.CasesDetailsPage.replace(':caseId', result as string),
        );
      }
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[...breadcrumbs, t('caseManagement.cases.addCase.label')]}
        onClickLink={() => LicenceStore.clearStore()}
      />
      <StyledPageTitle variant="h4">{t('caseManagement.cases.addCase.label')}</StyledPageTitle>
      {(isLoading || isLoadingPrices) && <Loader isLoading />}
      {!isLoading && licenceDetails && licenceDetails.legal && type && (
        <CaseAddForm
          LegalEntityid={licenceDetails.legal?.uniqueId}
          licenceId={licenceDetails.uniqueId}
          caseType={EAddCaseType.VARY}
          type={type}
          isFromLE={isFromLE || isFromLEList}
          submitForm={submitForm}
          backLink={backLink}
          isVary
        />
      )}
    </>
  );
};

export { LegalEntityLicenceVaryPage };
